'use client'

import { useMemo, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import { useInView } from 'react-intersection-observer'

import type { ChartConfig } from '~/src/components/ui/chart'
import { cn } from '~/src/lib/utils'
import { RADAR_CHART_CONTAINER_CLASS } from './reportChartContainers'
import type { RadarChartDatum } from './SeoReportRadarChart'
import { renderSeoReportLimitedMarkdown } from './SeoReportLimitedMarkdown'
import { SeoReportTruncatedCopy } from './SeoReportTruncatedCopy'

// The chart body carries the whole recharts dependency, so it stays out of
// the page bundle and only downloads once the section nears the viewport
// (see the useInView gate below). The placeholder mirrors the chart
// container's dimensions so the swap doesn't shift layout.
const RadarChartPlaceholder = () => (
  <div aria-hidden='true' className={RADAR_CHART_CONTAINER_CLASS} />
)

const SeoReportRadarChart = dynamic(
  () => import('./SeoReportRadarChart').then((m) => m.SeoReportRadarChart),
  { ssr: false, loading: RadarChartPlaceholder }
)

// color-token-exception: data-viz series colors, dynamically assigned per product
const DEFAULT_RADAR_COLORS = ['#E040C8', '#1AA8D4', '#8B7FE8', '#E14F0B']
const DEFAULT_TICK_VALUES = [20, 40, 60, 80, 100]

export interface RadarComparisonMetric {
  key: string
  label: string
}

export interface RadarComparisonSeries {
  id: string
  name: string
  values: Record<string, number>
  color?: string
}

export interface SeoReportRadarProductComparisonSectionProps {
  title?: string
  description?: string
  summary?: ReactNode
  metrics: RadarComparisonMetric[]
  series: RadarComparisonSeries[]
  className?: string
  maxValue?: number
  tickValues?: number[]
  headingVariant?: 'default' | 'report'
}

function clampScore(value: unknown, maxValue: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.max(0, Math.min(maxValue, value))
}

export function SeoReportRadarProductComparisonSection({
  title = 'How we scored the top 3',
  description = 'How the top 3 picks stack up across all scored criteria.',
  summary,
  metrics,
  series,
  className,
  maxValue = 100,
  tickValues = DEFAULT_TICK_VALUES,
  headingVariant = 'default',
}: SeoReportRadarProductComparisonSectionProps) {
  const { ref: chartInViewRef, inView: chartInView } = useInView({
    triggerOnce: true,
    rootMargin: '600px 0px',
  })
  const usesReportHeading = headingVariant === 'report'
  const HeadingTag = usesReportHeading ? 'h3' : 'h2'
  const normalizedSeries = useMemo(
    () =>
      series.map((item, index) => ({
        ...item,
        color:
          item.color ??
          DEFAULT_RADAR_COLORS[index % DEFAULT_RADAR_COLORS.length],
      })),
    [series]
  )

  const chartConfig = useMemo<ChartConfig>(() => {
    return normalizedSeries.reduce<ChartConfig>((config, item) => {
      config[item.id] = {
        label: item.name,
        color: item.color,
      }
      return config
    }, {})
  }, [normalizedSeries])

  const chartData = useMemo<RadarChartDatum[]>(
    () =>
      metrics.map((metric) => {
        const datum: RadarChartDatum = {
          metric: metric.label,
        }

        for (const item of normalizedSeries) {
          datum[item.id] = clampScore(item.values[metric.key], maxValue)
        }

        return datum
      }),
    [maxValue, metrics, normalizedSeries]
  )

  if (metrics.length === 0 || normalizedSeries.length === 0) {
    return null
  }

  return (
    <section className={cn('bg-white py-8 md:py-24', className)}>
      <div
        className={cn(
          'mx-auto flex max-w-[1280px] flex-col',
          usesReportHeading ? 'w-full items-stretch' : 'items-center md:px-8'
        )}
      >
        <div
          className={cn(
            'max-w-[768px]',
            usesReportHeading ? 'text-left' : 'text-center'
          )}
        >
          <HeadingTag
            className={
              usesReportHeading
                ? 'text-xl font-semibold leading-7 text-foreground'
                : 'font-semibold text-3xl md:text-5xl'
            }
          >
            {title}
          </HeadingTag>
          <p
            className={
              usesReportHeading
                ? 'mt-2 text-sm leading-6 text-foreground-secondary'
                : 'mt-5 opacity-60'
            }
          >
            {description}
          </p>
        </div>

        <div className='mt-6 w-full max-w-[1136px] rounded-[12px] bg-white'>
          <div className='flex flex-col gap-6 py-6'>
            <div ref={chartInViewRef} className='mx-auto w-full max-w-[760px]'>
              {chartInView ? (
                <SeoReportRadarChart
                  chartConfig={chartConfig}
                  chartData={chartData}
                  series={normalizedSeries}
                  maxValue={maxValue}
                  tickValues={tickValues}
                />
              ) : (
                <RadarChartPlaceholder />
              )}

              <div className='mt-5 flex flex-wrap items-center justify-center gap-5'>
                {normalizedSeries.map((item) => (
                  <div key={item.id} className='flex items-center gap-[6px]'>
                    <span
                      className='size-2 rounded-full'
                      style={{ backgroundColor: item.color }}
                    />
                    <p className='text-[13px] font-medium leading-none text-foreground-heading'>
                      {item.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {summary ? (
              <SeoReportTruncatedCopy
                maxHeight={400}
                className='mx-auto w-full max-w-[900px] text-base leading-7 text-foreground-secondary'
              >
                {typeof summary === 'string'
                  ? renderSeoReportLimitedMarkdown(summary)
                  : summary}
              </SeoReportTruncatedCopy>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}
